import * as constants from '../constants/calculatorConstants'
import {
  calculateAsRobotNode,
  calculateStudioWebNode,
  processAutomationHubProduct,
  processAutomationOpsProduct,
  processOrchestratorProduct,
  processPlatformProduct,
  processDocumentUnderstandingProduct,
  processActionCenterProduct,
  processTestManagerProduct,
  processAICenterProduct,
  processLLMGatewayProduct,
  processA4EProduct,
  processAutomationSolutionsProduct,
  processAgentHubProduct,
  processMaestroProduct,
  calculateMaestroPgContribution,
  handleDocumentUnderstanding,
  formatCpu,
  formatBytes,
  round2Cpu,
  round2Ram,
  getMultizonalFactor,
  ProductHandlerContext,
} from './productUtils';
import { getDistribution, getBaselineProfile } from '../adapters/distributionHelper';
import { getServiceResources, getProductResources, getProductOverride } from '../adapters/resourceAdapter';
import { resolveServices, stripServices, ServiceDef } from '../adapters/dependencyAdapter';
import { deriveSecondaryPlan, isApAvailable } from '../adapters/apSupportAdapter';
import { getEffectiveScalingProperties } from '../adapters/baselineLoadAdapter';
import { getRequiredDatabaseTypes, getProductNamesForDbType } from '../adapters/databaseAdapter';
import productServiceMapping from '../data/product-service-mapping.json';

export enum Profile {
  EvaluationOnly = "Evaluation Only",
  HAProduction = "Production - HA Ready",
  Lite = "Lite",
  AKSDefault = "AKS - Default",
  AKSLite = "AKS - Lite",
  AKSHA = "AKS - HA",
  EKSDefault = "EKS - Default",
  EKSLite = "EKS - Lite",
  EKSHA = "EKS - HA",
  OpenshiftDefault = "Openshift - Default",
  OpenshiftLite = "Openshift - Lite",
  OpenshiftHA = "Openshift - HA"
}

const workerType = "Worker";
const workerTypeTooltip = "Kubernetes Worker Node";
const serverType = "Server";
const serverTypeTooltip = "Kubernetes Control Plane Node";
const agentType = "Agent";
const agentTypeTooltip = "Kubernetes Worker Node";
const asRobotsType = "Dedicated (AS Robots)";
const asRobotsTypeTooltip = "Dedicated node for Automation Suite Robots";
const AS_ROBOT_NODE_CPU = 8000; // 8 vCPU in millicores
const AS_ROBOT_NODE_RAM = 32 * 1024 * 1024 * 1024; // 32 GiB in bytes
const duType = "Dedicated (Document Understanding)";
const duTypeTooltip = "Dedicated GPU node for Document Understanding";
const pmType = "Dedicated SQL Server (Process Mining)";
const pmTypeTooltip = "SQL Server for Process Mining";
const nfsType = "NFS";
const nfsTypeTooltip = "NFS Server for Backup Process";
const sqlType = "Shared SQL Server";
const sqlTypeTooltip = "SQL Server for selected products";
const postgresType = "PostgreSQL Server (Process Mining)";
const postgresTypeTooltip = "PostgreSQL Server for Process Mining";

const STANDARD_NODE_SIZES = [16, 32, 48, 64, 96, 128, 192, 256]; // vCPU

// Sizing headroom applied on top of the raw service totals.
// Total = (fabric + allServices) + BUFFER_RATE*(fabric + allServices) + HEADROOM_RATE*allServices,
// where allServices = selected products + Shared Suite Capabilities (everything shown as services).
const BUFFER_RATE = 0.20;   // 20% buffer on the full total (services + platform + fabric)
const HEADROOM_RATE = 0.10; // extra 10% headroom on all services (products + Shared Suite Capabilities)

function pushProductDocLink(details: string[], productKey: string): void {
  const docUrl = constants.PRODUCT_DOC_URLS[productKey];
  if (docUrl) {
    details.push(`* Please check the [documentation](${docUrl}) for more details on additional supported features and their requirements.`);
  }
}

function getNodeSize(requiredCpuVCPU: number): { cpu: number; ram: number; exceeded: boolean } {
  const minCpu = Math.max(requiredCpuVCPU, 16); // floor: 16 vCPU minimum
  for (const size of STANDARD_NODE_SIZES) {
    if (size >= minCpu) {
      return { cpu: size, ram: size * 2, exceeded: false }; // RAM = CPU * 2
    }
  }
  // Exceeds largest standard size
  const largest = STANDARD_NODE_SIZES[STANDARD_NODE_SIZES.length - 1];
  return { cpu: largest, ram: largest * 2, exceeded: true };
}

/**
 * Render per-service line items into the details array.
 */
function renderServiceLineItems(
  services: string[],
  distribution: string,
  baselineProfile: string,
  details: string[],
  serviceDisplayNames: Map<string, string>
): void {
  for (const svc of services) {
    const res = getServiceResources(svc, distribution, baselineProfile);
    if (res.cpu > 0 || res.ram > 0) {
      const name = serviceDisplayNames.get(svc) || svc;
      details.push(`  * ${name}: **${formatCpu(res.cpu, 2)}** / **${formatBytes(res.ram * 1024 * 1024, 2)}**`);
    }
  }
}

/**
 * A single service's line equals the product Total, so on its own it is redundant and is
 * dropped — leaving just the Total. But when scaling / on-demand adds resources, the base line
 * is kept so the section reads base + addition = Total (the addition is inclusive of the
 * Total). Multi-service breakdowns and aggregate overrides are left untouched.
 *
 * `serviceLineCount` is how many lines renderServiceLineItems pushed starting at `svcStart`.
 */
function dropRedundantSingleServiceLine(
  details: string[],
  svcStart: number,
  serviceLineCount: number,
  hasOverride: boolean,
  hasAddition: boolean
): void {
  if (serviceLineCount === 1 && !hasOverride && !hasAddition) {
    details.splice(svcStart, 1);
  }
}

/**
 * Apply the sizing headroom to a base total: a 20% buffer on the whole total plus a 10%
 * headroom on all services (products + Shared Suite Capabilities). Both results are rounded to 2 decimals.
 */
function applyBufferAndHeadroom(
  baseCpu: number,
  baseRam: number,
  zCpu: number,
  zRam: number
): { cpu: number; ram: number } {
  return {
    cpu: round2Cpu(baseCpu * (1 + BUFFER_RATE) + zCpu * HEADROOM_RATE),
    ram: round2Ram(baseRam * (1 + BUFFER_RATE) + zRam * HEADROOM_RATE),
  };
}

/**
 * Insert the "* Total: ..." headline as the first bullet of a product section (right after its
 * heading), showing the product's full contribution (base services + any scaling / on-demand
 * additions). Always shown, so every product section leads with its total.
 */
function insertProductTotalLine(
  details: string[],
  headingIndex: number,
  totalCpu: number,
  totalRam: number
): void {
  details.splice(
    headingIndex + 1,
    0,
    `* Total: **${formatCpu(totalCpu, 2)}** / **${formatBytes(totalRam * 1024 * 1024, 2)}**`
  );
}

/**
 * Render the "Automation Suite Platform requirement" section: heading, CPU/RAM value and the
 * shared explanation. Profile-specific notes (HAA, shared-cluster) are pushed by the caller.
 */
function pushPlatformRequirementSection(details: string[], cpu: number, ram: number): void {
  details.push(`#### Automation Suite Platform requirement`);
  details.push(`* Needs **${formatCpu(cpu, 2)}** / **${formatBytes(ram * 1024 * 1024, 2)}**.`);
  details.push(`* Covers the Automation Suite components plus headroom for selected services' resources like jobs / cronjobs and upgrade scenarios, which are not always up and running but draw from this capacity whenever they run.`);
}

/**
 * Dispatch a product handler. Every handler receives the same uniform
 * ProductHandlerContext as its final argument — no per-product branching.
 */
function dispatchProductHandler(
  product: ProductOptionInterface,
  details: string[],
  totalCpu: number,
  totalRam: number,
  totalPvc: number,
  productHandlers: Map<string, Function>,
  context: ProductHandlerContext,
): { totalCpu: number; totalRam: number; totalPvc: number } | void {
  const handler = productHandlers.get(product.key);
  if (!handler) return;
  return handler(product, details, totalCpu, totalRam, totalPvc, context);
}

/**
 * Feeds the handler floored effective inputs: `default` = pinned baseline
 * (stored ignored); lite/ha = max(stored, floor). Never mutates formData —
 * shallow copy; non-numeric flags preserved by getEffectiveScalingProperties.
 */
function toEffectiveProduct(
  product: ProductOptionInterface,
  baselineProfile: string,
): ProductOptionInterface {
  const eff = getEffectiveScalingProperties(product.key, baselineProfile, product.properties || {});
  return { ...product, properties: { ...(product.properties || {}), ...eff } };
}

/**
 * Compute CPU/RAM for a product: check overrides first, then sum per-service.
 */
function computeProductResources(
  services: string[],
  productKey: string,
  distribution: string,
  baselineProfile: string,
): { cpu: number; ram: number } {
  const override = getProductOverride(productKey, distribution, baselineProfile);
  let cpu = 0, ram = 0;
  if (override) {
    cpu = override.cpu;
    ram = override.ram;
  } else {
    for (const svc of services) {
      const res = getServiceResources(svc, distribution, baselineProfile);
      cpu += res.cpu;
      ram += res.ram;
    }
  }
  return { cpu, ram };
}

/**
 * Inner per-site sizing pass. Returns a SiteResult-shaped object (or with an `error` field).
 */
const computeSiteRequirements = (
  formData: CalculatorFormDataType,
  apSite?: 'primary' | 'secondary',
  excludeServices?: Set<string>
): SiteResult & { error?: string } => {

  const convertProfile = (profile: string): Profile => {
    switch (profile) {
      case "eval":
        return Profile.EvaluationOnly;
      case "ha":
        return Profile.HAProduction;
      case "lite":
        return Profile.Lite;
      case "air-aks":
        switch (formData.highLevelParameters.aksProfile) {
          case "lite":
            return Profile.AKSLite;
          case "ha":
            return Profile.AKSHA;
          default:
            return Profile.AKSDefault;
        }
      case "air-eks":
        switch (formData.highLevelParameters.eksProfile) {
          case "lite":
            return Profile.EKSLite;
          case "ha":
            return Profile.EKSHA;
          default:
            return Profile.EKSDefault;
        }
      case "air-openshift":
        switch (formData.highLevelParameters.openshiftProfile) {
          case "lite":
            return Profile.OpenshiftLite;
          case "ha":
            return Profile.OpenshiftHA;
          default:
            return Profile.OpenshiftDefault;
        }
      default:
        return Profile.EvaluationOnly;
    }
  };

  let profile = convertProfile(formData.highLevelParameters.deploymentType);

  // Compute distribution and baseline profile for adapter lookups
  const distribution = getDistribution(formData.highLevelParameters.deploymentType);
  const baselineProfile = getBaselineProfile(formData.highLevelParameters.deploymentType, formData.highLevelParameters);

  // A product's baseline bucket is derived HERE and nowhere else. In a Lite
  // deployment each product may run under its own sub-profile (ToggleProfile);
  // platform (Shared Suite) always sizes at the lite bucket.
  const isLiteDeployment = formData.highLevelParameters.deploymentType === 'lite';
  const getProductBaselineProfile = (product?: ProductOptionInterface): string => {
    if (!isLiteDeployment) return baselineProfile;
    if (!product || product.key === constants.PLATFORM_PRODUCT_OPTION.key) return getBaselineProfile('lite');
    return getBaselineProfile(product.profile || 'lite');
  };

  // Effective products are computed ONCE where form data enters the sizing pass.
  // Every consumer that sizes anything (enable flags, dedicated-node calculators,
  // product handlers) must read these — never raw formData.products.
  const effectiveProducts = formData.products.map(
    (p) => toEffectiveProduct(p, getProductBaselineProfile(p)),
  );

  // Resolve all services (including transitive deps) for the selected products.
  // On the A/P secondary pass, strip banned services right here — the single
  // seam where resolution output is born — so every downstream consumer
  // (per-product sizing, platform services, DB-type derivation) sees the
  // stripped set. This catches a banned service force-included by a
  // partialSelection owner (e.g. platform's taas), which the product-level
  // filter cannot remove.
  const explicitKeys = formData.explicitKeys;
  const resolved = resolveServices(explicitKeys, distribution);
  const { productServices, resolvedServices: allResolvedServices } =
    excludeServices && excludeServices.size > 0
      ? stripServices(resolved, excludeServices)
      : resolved;

  // Build service display name lookup from product-service-mapping
  const serviceDisplayNames = new Map<string, string>();
  const psmProducts = (productServiceMapping as any).products;
  Object.keys(psmProducts).forEach((key: string) => {
    const def = psmProducts[key];
    def.services.forEach((svc: ServiceDef) => {
      serviceDisplayNames.set(svc.name, svc.displayName);
    });
  });

  const isASAirProfile = (p: Profile): boolean =>
    [Profile.AKSDefault, Profile.AKSLite, Profile.AKSHA, Profile.EKSDefault, Profile.EKSLite, Profile.EKSHA, Profile.OpenshiftDefault, Profile.OpenshiftLite, Profile.OpenshiftHA].includes(p);
  // OpenShift deployments do not include Fabric in their resource calculations.
  const isOpenShift = formData.highLevelParameters.deploymentType === "air-openshift";
  // Shared Cluster (AKS/EKS only): the user is bringing their own managed cluster
  // that already provides the Service Fabric layer, so exclude fabric sizing from
  // the total. The toggle is only surfaced in HighLevelConfig for air-aks/air-eks.
  const isSharedCluster =
    !!formData.highLevelParameters.sharedCluster &&
    (formData.highLevelParameters.deploymentType === "air-aks" ||
      formData.highLevelParameters.deploymentType === "air-eks");

  let onlyCoreProducts = true;
  let isAutomationSuiteRobotsEnabled = false;
  let isStudioWebEnabled = false;
  let isISEnabled = false;
  let isProcessMiningEnabled = false;
  let dudisk: string[] = [];
  let sqldisk: string[] = [];
  let pmdisk: string[] = [];
  let asRobotdisk: string[] = [];

  if (!isASAirProfile(profile)) {
    dudisk.push("256 GiB SSD");
    sqldisk.push("256 GiB SSD");
  }

  const duNode = (count: number): NodeDataType => {
    return {
      nodeType: duType,
      nodeTypeTooltip: duTypeTooltip,
      nodeCount: count.toString(),
      cpu: "8 vCPU",
      ram: "52 GiB",
      drives: dudisk,
      gpu: "NVIDIA"
    }
  };

  const duModernExperienceNode = (count: number): NodeDataType => {
    return {
      nodeType: duType,
      nodeTypeTooltip: duTypeTooltip,
      nodeCount: count.toString(),
      cpu: "4 vCPU",
      ram: "28 GiB",
      drives: dudisk,
      gpu: "NVIDIA"
    }
  };


  const nfsNode: NodeDataType = {
    nodeType: nfsType,
    nodeTypeTooltip: nfsTypeTooltip,
    nodeCount: "1",
    cpu: "4 vCPU",
    ram: "8 GiB",
    drives: [
      "2 TiB SSD"
    ],
  };

  const sqlNode = {
    nodeType: sqlType,
    nodeTypeTooltip: sqlTypeTooltip,
    nodeCount: "1",
    cpu: "8 vCPU",
    ram: "32 GiB",
    drives: sqldisk,
  };

  const postgresNode = {
    nodeType: postgresType,
    nodeTypeTooltip: postgresTypeTooltip,
    nodeCount: "1",
    cpu: "4 vCPU",
    ram: "16 GiB",
    drives: [
        "128 GiB SSD",
    ],
  };

  const pmProductSel = effectiveProducts.find(p => p.key === constants.PROCESS_MINING_PRODUCT_OPTION.key);
  const maestroProduct = effectiveProducts.find(p => p.key === constants.MAESTRO_PRODUCT_OPTION.key);
  const a4dProduct = effectiveProducts.find(p => p.key === constants.A4D_PRODUCT_OPTION.key);
  const A4D_PG_CONNECTIONS = 100;

  // Per-product PostgreSQL contribution to the shared PG server. The pg line for each
  // product is rendered in the Per Service section under that product; the aggregated
  // node and combined totals are rendered in the Recommendations section.
  // Maestro's contribution is computed from workload inputs below (workload-driven, not static).
  const PG_CONTRIBUTIONS: Record<string, { cpu: number; ram: number; disk: number; conn: number; iops: number; cpuLabel: string }> = {
    [constants.PROCESS_MINING_PRODUCT_OPTION.key]: { cpu: 4, ram: 16, disk: 128, conn: 0, iops: 500, cpuLabel: "4 vCPU" },
    [constants.A4D_PRODUCT_OPTION.key]:            { cpu: 2, ram: 8,  disk: 128, conn: A4D_PG_CONNECTIONS,     iops: 100, cpuLabel: "2 vCPU" },
  };

  if (maestroProduct) {
    // In a Lite deployment each product can run under its own profile via ToggleProfile;
    // otherwise Maestro inherits the overall deployment profile.
    const maestroProfile = profile === Profile.Lite ? convertProfile(maestroProduct.profile) : profile;
    const mc = calculateMaestroPgContribution(maestroProduct, maestroProfile);
    PG_CONTRIBUTIONS[constants.MAESTRO_PRODUCT_OPTION.key] = {
      cpu: mc.cpu,
      ram: mc.ram,
      disk: mc.disk,
      conn: mc.connections,
      iops: mc.iops,
      cpuLabel: `${mc.cpu} vCPU`,
    };
  }

  function getProductPostgresLine(productKey: string): string | null {
    const c = PG_CONTRIBUTIONS[productKey];
    if (!c) return null;
    const parts = [`**${c.cpuLabel}**`, `**${c.ram} GiB**`, `**${c.disk} GiB SSD**`];
    if (c.conn > 0) parts.push(`**${c.conn} connections**`);
    if (c.iops > 0) parts.push(`**${c.iops} IOPS**`);
    return `* Requires a **Shared PostgreSQL Server** contribution of ${parts.join(' / ')}.`;
  }

  // Build a shared PostgreSQL Server node from whichever of PM, Maestro, A4D are selected.
  // Returns null if none of them are selected (caller may fall back to the legacy generic node).
  function buildSharedPostgresNode(): { node: NodeDataType; detailLines: string[] } | null {
    if (!pmProductSel && !maestroProduct && !a4dProduct) return null;
    const contributors: string[] = [];
    let cpu = 0, ram = 0, disk = 0, conn = 0, iops = 0;
    const add = (key: string, name: string) => {
      const c = PG_CONTRIBUTIONS[key];
      cpu += c.cpu; ram += c.ram; disk += c.disk; conn += c.conn; iops += c.iops;
      contributors.push(name);
    };
    if (pmProductSel)    add(constants.PROCESS_MINING_PRODUCT_OPTION.key, constants.PROCESS_MINING_PRODUCT_OPTION.name);
    if (maestroProduct)  add(constants.MAESTRO_PRODUCT_OPTION.key,        constants.MAESTRO_PRODUCT_OPTION.name);
    if (a4dProduct)      add(constants.A4D_PRODUCT_OPTION.key,            constants.A4D_PRODUCT_OPTION.name);

    const label = contributors.join(', ');
    const node: NodeDataType = {
      nodeType: `PostgreSQL Server (${label})`,
      nodeTypeTooltip: `Shared PostgreSQL Server for ${label}`,
      nodeCount: "1",
      cpu: `${cpu} vCPU`,
      ram: `${ram} GiB`,
      drives: [`${disk} GiB SSD`],
    };
    if (conn > 0) node.maxConnections = `${conn} connections`;
    if (iops > 0) node.iops = `${iops} IOPS`;

    const totalParts = [`**${cpu} vCPU**`, `**${ram} GiB RAM**`, `**${disk} GiB SSD**`];
    if (conn > 0) totalParts.push(`**${conn} max connections**`);
    if (iops > 0) totalParts.push(`**${iops} IOPS**`);
    const detailLines = [
      `* **${label}** share a single **PostgreSQL Server**. Combined total: ${totalParts.join(' / ')} — see each product in the Per Service section for its individual contribution.`,
    ];
    return { node, detailLines };
  }

  function calculatePMNode(pmProduct: ProductOptionInterface | undefined, isProcessMiningEnabled: boolean): NodeDataType {
    let pmDiskSpace = 0;
    let pmTempDbSize = 0;
    let pmRam = 0;
    if (isProcessMiningEnabled && pmProduct) {
      if (pmProduct.properties != undefined) {
        let modelBasedPM = !!pmProduct.properties["modelBasedApplications"];
        let base = pmProduct.properties["eventsInMillions"] * pmProduct.properties["caseAndEventFields"] * 0.012;
        if (modelBasedPM) {
          base = base * 5.4;
        }
        pmDiskSpace = Math.ceil(base * 12);
        pmTempDbSize = Math.ceil(base * 3);
        pmRam = Math.ceil((base * 2) + 16);
      }
    }

    if (!isASAirProfile(profile)) {
      pmdisk.push(Math.max(pmDiskSpace, 256) + " GiB Disk Space", Math.max(pmTempDbSize, 128) + " GiB TempDb Database Size");
    }

    const pmNode: NodeDataType = {
      nodeType: pmType,
      nodeTypeTooltip: pmTypeTooltip,
      nodeCount: "1",
      cpu: "16 vCPU (Minimum: 8 vCPU)",
      ram: Math.max(pmRam, 32) + " GiB",
      drives: pmdisk,
    };
    return pmNode;
  }

  function populateDUModernExperienceGPUNodeInfo(isModernExperienceEnabled: boolean, modernExperienceGpuInfo: Map<string, number>, nodes: Array<NodeDataType>) {
    if (isModernExperienceEnabled) {
      let modernExperienceGpuCount = 0;
      modernExperienceGpuInfo.forEach((value, key) => {
        modernExperienceGpuCount += value;
        details.push(`* **Document Understanding** needs ${value} [dedicated agent](https://docs.uipath.com/automation-suite/docs/adding-a-dedicated-agent-node-with-gpu-support) node for **${key}**`)
      })
      nodes.push(duModernExperienceNode(modernExperienceGpuCount))
    }
  }

  let isAICenterTrainingEnabled = false;
  for (let product of effectiveProducts) {
    if (product.isCoreProduct === 0) {
      onlyCoreProducts = false;
    }
    if (product.key === constants.AUTOMATION_SUITE_ROBOTS_PRODUCT_OPTION.key) {
      isAutomationSuiteRobotsEnabled = true;
    }
    if (product.key === constants.STUDIO_WEB_PRODUCT_OPTION.key) {
      isStudioWebEnabled = true;
    }
    if (product.key === constants.INTEGRATION_SERVICES_PRODUCT_OPTION.key) {
      isISEnabled = true;
    }
    if (product.key === constants.PROCESS_MINING_PRODUCT_OPTION.key) {
      isProcessMiningEnabled = true;
    }
    if (product.key === constants.AI_CENTER_PRODUCT_OPTION.key && product.properties !== undefined && +product.properties["numberOfPipelines"] > 0) {
      isAICenterTrainingEnabled = true;
    }
  }

  let osDisk = "256 GiB SSD";

  // Document Understanding GPU recommendation
  const {duGpuCount, ocrGpuRequired, modernExperienceGpuInfo} = handleDocumentUnderstanding(effectiveProducts);
  let isModernExperienceEnabled = modernExperienceGpuInfo.size > 0;

  if (isModernExperienceEnabled) {
    osDisk = "512 GiB SSD";
  }

  // OS Disk for air profiles (AKS, EKS, OpenShift): default 100 GiB, 256 GiB if DU modern experience or AI Center training is enabled
  let airOsDisk = "100 GiB";
  if (isModernExperienceEnabled || isAICenterTrainingEnabled) {
    airOsDisk = "256 GiB";
  }

  // Automation Suite Robots additional node recommendation
  const {
    asRobotNodeCpu,
    asRobotNodeRam,
    asRobotNodeDisk,
    isAdditionalNodeNeededEval
  } = calculateAsRobotNode(effectiveProducts, isAutomationSuiteRobotsEnabled);

  // Studio Web serverless node recommendation
  const {
    studioWebNodeCpu,
    studioWebNodeRam,
    studioWebNodeDisk,
  } = calculateStudioWebNode(effectiveProducts, isStudioWebEnabled);

  // Per-product contribution to the shared dedicated serverless node (AS Robots + Studio Web).
  // Rendered inline in each product's Per Service section; skipped when contribution is zero.
  function getServerlessContributionLine(productKey: string): string | null {
    if (productKey === constants.AUTOMATION_SUITE_ROBOTS_PRODUCT_OPTION.key) {
      if (!isAutomationSuiteRobotsEnabled || asRobotNodeCpu === 0) return null;
      return `* Adds **${formatCpu(asRobotNodeCpu)}** / **${formatBytes(asRobotNodeRam)}** / **${asRobotNodeDisk} GiB SSD** to the dedicated serverless node.`;
    }
    if (productKey === constants.STUDIO_WEB_PRODUCT_OPTION.key) {
      if (!isStudioWebEnabled || studioWebNodeCpu === 0) return null;
      const swProduct = effectiveProducts.find(p => p.key === constants.STUDIO_WEB_PRODUCT_OPTION.key);
      const concurrentWorkflows = +(swProduct?.properties?.concurrentWorkflows || 0);
      return `* Adds **${formatCpu(studioWebNodeCpu)}** / **${formatBytes(studioWebNodeRam)}** / **${studioWebNodeDisk} GiB SSD** to the dedicated serverless node (${concurrentWorkflows} concurrent workflows × 2 vCPU / 4 GiB / 4 GiB each).`;
    }
    return null;
  }

  // Process Mining additional sql requirement
  const pmNode = calculatePMNode(pmProductSel, isProcessMiningEnabled);


  let details = new Array<string>();
  let dataDisk = "512 GiB SSD";

  const productHandlers = new Map<string, Function>([
    [constants.ORCHESTRATOR_PRODUCT_OPTION.key, processOrchestratorProduct],
    [constants.PLATFORM_PRODUCT_OPTION.key, processPlatformProduct],
    [constants.AUTOMATION_OPS_PRODUCT_OPTION.key, processAutomationOpsProduct],
    [constants.AUTOMATION_HUB_PRODUCT_OPTION.key, processAutomationHubProduct],
    [constants.DOCUMENT_UNDERSTANDING_AI_CENTER_PRODUCT_OPTION.key, processDocumentUnderstandingProduct],
    [constants.DOCUMENT_UNDERSTANDING_MODERN_PROJECTS_PRODUCT_OPTION.key, processDocumentUnderstandingProduct],
    [constants.ACTION_CENTER_PRODUCT_OPTION.key, processActionCenterProduct],
    [constants.TEST_MANAGER_PRODUCT_OPTION.key, processTestManagerProduct],
    [constants.AI_CENTER_PRODUCT_OPTION.key, processAICenterProduct],
    [constants.AITL_PRODUCT_OPTION.key, processLLMGatewayProduct],
    [constants.A4E_PRODUCT_OPTION.key, processA4EProduct],
    [constants.AUTOMATION_SOLUTIONS_PRODUCT_OPTION.key, processAutomationSolutionsProduct],
    [constants.AGENT_HUB_PRODUCT_OPTION.key, processAgentHubProduct],
    [constants.MAESTRO_PRODUCT_OPTION.key, processMaestroProduct],
  ]);

  // Single-node Evaluation Only
  if (profile === Profile.EvaluationOnly) {
    let nodes = new Array<NodeDataType>();
    let sqlNodes = new Array<NodeDataType>()

    // --- Step 1: Compute actual resource totals ---
    let evalTotalCpu = 0;
    let evalTotalRam = 0;
    let evalTotalPvc = 0;
    let evalTotalCpuBeforeBuffer = 0;
    let evalTotalRamBeforeBuffer = 0;

    details.push("## Resource Requirements");
    details.push("### Per Service");

    for (let product of effectiveProducts) {
      // Platform is handled in the Shared Suite Capabilities section below
      if (product.key === constants.PLATFORM_PRODUCT_OPTION.key) continue;

      const services = productServices.get(product.key) || [];
      const { cpu, ram } = computeProductResources(
        services, product.key, distribution, baselineProfile,
      );
      const override = getProductOverride(product.key, distribution, baselineProfile);

      evalTotalCpu += cpu;
      evalTotalRam += ram;

      const evalHeadingIndex = details.length;
      details.push(`#### ${product.name}`);
      // Track this product's additional (on-demand + handler) contribution, for the Total line.
      let evalAddCpu = 0;
      let evalAddRam = 0;

      // Add AI Center note as the first point in the section (same as multi-node / HA)
      if (product.key === constants.AI_CENTER_PRODUCT_OPTION.key) {
        details.push("* **Note:** Only incase you have overridden CPU and Memory limits for your AI Center Skill. Use VM with CPU = Skill CPU size + 2 vCPU and RAM = Skill RAM size + 2GB based on your requirement.");
      }

      // Base per-service line(s). A lone one is dropped later unless scaling keeps it useful.
      const evalSvcStart = details.length;
      if (!override) {
        renderServiceLineItems(services, distribution, baselineProfile, details, serviceDisplayNames);
      }
      const evalServiceLineCount = details.length - evalSvcStart;

      const evalPgLine = getProductPostgresLine(product.key);
      if (evalPgLine) details.push(evalPgLine);

      const evalServerlessLine = getServerlessContributionLine(product.key);
      if (evalServerlessLine) details.push(evalServerlessLine);

      const evalPreHandlerCpu = evalTotalCpu;
      const evalPreHandlerRam = evalTotalRam;
      const evalResult = dispatchProductHandler(product, details, evalTotalCpu, evalTotalRam, evalTotalPvc, productHandlers, { profile, services, baselineProfile });
      if (evalResult) {
        evalTotalCpu = round2Cpu(evalResult.totalCpu);
        evalTotalRam = round2Ram(evalResult.totalRam);
        evalTotalPvc = evalResult.totalPvc || evalTotalPvc;
      }
      evalAddCpu += evalTotalCpu - evalPreHandlerCpu;
      evalAddRam += evalTotalRam - evalPreHandlerRam;

      dropRedundantSingleServiceLine(details, evalSvcStart, evalServiceLineCount, !!override, evalAddCpu > 0 || evalAddRam > 0);
      insertProductTotalLine(details, evalHeadingIndex, cpu + evalAddCpu, ram + evalAddRam);

      pushProductDocLink(details, product.key);
    }

    // Shared Suite Capabilities (platform services resolved via graph)
    {
      const platformProduct = effectiveProducts.find((p: any) => p.key === constants.PLATFORM_PRODUCT_OPTION.key);
      const platformServices = productServices.get(constants.PLATFORM_PRODUCT_OPTION.key) || [];
      let platformCpu = 0;
      let platformRam = 0;
      for (const svc of platformServices) {
        const res = getServiceResources(svc, distribution, baselineProfile);
        platformCpu += res.cpu;
        platformRam += res.ram;
      }
      evalTotalCpu += platformCpu;
      evalTotalRam += platformRam;

      const evalPlatHeadingIndex = details.length;
      details.push(`#### Shared Suite Capabilities`);
      const evalPlatSvcStart = details.length;
      renderServiceLineItems(platformServices, distribution, baselineProfile, details, serviceDisplayNames);
      const evalPlatServiceLineCount = details.length - evalPlatSvcStart;

      const evalPlatPreHandlerCpu = evalTotalCpu;
      const evalPlatPreHandlerRam = evalTotalRam;
      if (platformProduct) {
        const platResult = dispatchProductHandler(platformProduct, details, evalTotalCpu, evalTotalRam, evalTotalPvc, productHandlers, { profile, services: platformServices, baselineProfile });
        if (platResult) {
          evalTotalCpu = round2Cpu(platResult.totalCpu);
          evalTotalRam = round2Ram(platResult.totalRam);
          evalTotalPvc = platResult.totalPvc || evalTotalPvc;
        }
      }
      const evalPlatAddCpu = evalTotalCpu - evalPlatPreHandlerCpu;
      const evalPlatAddRam = evalTotalRam - evalPlatPreHandlerRam;

      dropRedundantSingleServiceLine(details, evalPlatSvcStart, evalPlatServiceLineCount, false, evalPlatAddCpu > 0 || evalPlatAddRam > 0);
      insertProductTotalLine(details, evalPlatHeadingIndex, platformCpu + evalPlatAddCpu, platformRam + evalPlatAddRam);
    }

    // "All services" = user-selected products + Shared Suite Capabilities, captured before
    // fabric is added. The extra 10% headroom is applied to this sum.
    const evalAllServicesCpu = evalTotalCpu;
    const evalAllServicesRam = evalTotalRam;

    // Fabric services (separate from Shared Suite Capabilities). Fabric is still counted
    // in the total, but is no longer shown as its own line — its footprint together with
    // the buffer headroom is surfaced as the "Automation Suite Platform requirement" below.
    const evalFabricOverride = getProductOverride('fabric', distribution, baselineProfile);
    const evalFabricReqs = evalFabricOverride || getProductResources('fabric', distribution, baselineProfile);
    evalTotalCpu += evalFabricReqs.cpu;
    evalTotalRam += evalFabricReqs.ram;

    // Total = (fabric + allServices) + 20% of that + 10% of allServices.
    // "Actual" shows the services only; the Automation Suite Platform requirement is
    // everything else (fabric + buffer headroom) = Total − services, so the two reconcile.
    const evalActualCpu = evalTotalCpu;
    const evalActualRam = evalTotalRam;
    const evalBuffered = applyBufferAndHeadroom(evalActualCpu, evalActualRam, evalAllServicesCpu, evalAllServicesRam);

    pushPlatformRequirementSection(details, evalBuffered.cpu - evalAllServicesCpu, evalBuffered.ram - evalAllServicesRam);
    details.push(`* **High Availability Add-on (HAA)** can be enabled in the same configuration without adding additional capacity. Learn more in [Automation Suite - High Availability Add-on configuration](https://docs.uipath.com/automation-suite/automation-suite/2.2510/installation-guide/high-availability-add-on).`);

    evalTotalCpuBeforeBuffer = evalActualCpu;
    evalTotalRamBeforeBuffer = evalActualRam;
    evalTotalCpu = evalBuffered.cpu;
    evalTotalRam = evalBuffered.ram;

    // Total summary: services + Automation Suite Platform requirement = Total Requirement.
    details.push("### Total");
    details.push(`* Actual (services): **${formatCpu(evalAllServicesCpu)}** / **${formatBytes(evalAllServicesRam * 1024 * 1024)}**`);
    details.push(`* Automation Suite Platform requirement: **${formatCpu(evalBuffered.cpu - evalAllServicesCpu)}** / **${formatBytes((evalBuffered.ram - evalAllServicesRam) * 1024 * 1024)}**`);
    details.push(`* Total Requirement: **${formatCpu(evalTotalCpu)}** / **${formatBytes(evalTotalRam * 1024 * 1024)}**`);

    if (evalTotalPvc > 0) {
      details.push(`* Additional dedicated PVC storage: **${evalTotalPvc}** GiB total.`);
    }

    // --- Step 2: Dynamically size the node based on buffered totals ---
    const bufferedCpuVCPU = evalTotalCpu / 1000;
    const bufferedRamGiB = evalTotalRam / 1024;
    const nodeSize = getNodeSize(Math.max(bufferedCpuVCPU, bufferedRamGiB / 2));
    const nodeCpu = nodeSize.cpu * 1000;         // millicores
    const nodeRam = nodeSize.ram * 1024 * 1024 * 1024; // bytes

    // --- Step 3: Build node recommendation ---
    details.push("## Server Node");
    if (nodeSize.exceeded) {
      details.push(`**Warning:** Your requirements exceed the largest single-node size (128 vCPU / 256 GiB). We recommend switching to a Multi Node deployment.`);
    }
    details.push(`For the Single-node Evaluation profile, one server node: **${formatCpu(nodeCpu)}** / **${formatBytes(nodeRam)}**`);

    nodes.push({
      nodeType: serverType,
      nodeTypeTooltip: serverTypeTooltip,
      nodeCount: "1",
      cpu: formatCpu(nodeCpu),
      ram: formatBytes(nodeRam),
      drives: [
        "16 GiB SSD",
        osDisk,
        dataDisk,
      ],
    });

    if (duGpuCount > 0 || isModernExperienceEnabled || isAdditionalNodeNeededEval) {
      details.push("## Dedicated Nodes")
    }
    if (duGpuCount > 0) {
      nodes.push(duNode(duGpuCount))
      if (ocrGpuRequired) {
        details.push(`* **Document Understanding** needs a [dedicated agent](https://docs.uipath.com/automation-suite/docs/adding-a-dedicated-agent-node-with-gpu-support) node to handle more than **60 OCR pages per hour**.`)
      }
    }

    populateDUModernExperienceGPUNodeInfo(isModernExperienceEnabled, modernExperienceGpuInfo, nodes);

    const serverlessNodeCpu = asRobotNodeCpu + studioWebNodeCpu;
    const serverlessNodeRam = asRobotNodeRam + studioWebNodeRam;
    const serverlessNodeDisk = asRobotNodeDisk + studioWebNodeDisk;
    const isServerlessNodeNeededEval = isAdditionalNodeNeededEval || (isStudioWebEnabled && studioWebNodeCpu > 0);

    if (isServerlessNodeNeededEval) {
      const serverlessNodeCount = Math.ceil(Math.max(serverlessNodeCpu / AS_ROBOT_NODE_CPU, serverlessNodeRam / AS_ROBOT_NODE_RAM));
      const serverlessProducts: string[] = [];
      if (isAdditionalNodeNeededEval) serverlessProducts.push("Automation Suite Robots");
      if (isStudioWebEnabled && studioWebNodeCpu > 0) serverlessProducts.push("Studio Web");
      const serverlessTooltip = `Node for ${serverlessProducts.join(' and ')}`;
      nodes.push({
        nodeType: asRobotsType,
        nodeTypeTooltip: serverlessTooltip,
        nodeCount: serverlessNodeCount.toString(),
        cpu: "8 vCPU",
        ram: "32 GiB",
        drives: [
          "256 GiB SSD",
          serverlessNodeDisk + " GiB SSD"
        ],
      });
      details.push(`* **${serverlessProducts.join(' and ')}** needs ${serverlessNodeCount} [dedicated agent](https://docs.uipath.com/automation-suite/automation-suite/2024.10/installation-guide/hardware-and-software-requirements#additional-automation-suite-robots-requirements) node${serverlessNodeCount > 1 ? 's' : ''}.`)
    }

    const evalDbTypes = getRequiredDatabaseTypes(allResolvedServices);
    if (isProcessMiningEnabled) {
      sqlNodes.push(pmNode);
      details.push("## Separated SQL Server")
      details.push(`* **Process Mining** requires a dedicated SQL Server node with **${pmNode.cpu}** / **${pmNode.ram}**.`)
    }
    if (evalDbTypes.has('postgresql') || pmProductSel || maestroProduct || a4dProduct) {
      const shared = buildSharedPostgresNode();
      if (shared) {
        sqlNodes.push(shared.node);
        details.push(...shared.detailLines);
      } else if (evalDbTypes.has('postgresql')) {
        sqlNodes.push(postgresNode);
        const pgProducts = getProductNamesForDbType(productServices, formData.products, 'postgresql');
        const pgProductLabel = pgProducts.length > 0 ? pgProducts.join(', ') : 'Process Mining';
        details.push(`* **${pgProductLabel}** requires a PostgreSQL Server node with **${postgresNode.cpu}** / **${postgresNode.ram}**, see [database requirements](https://docs.uipath.com/automation-suite/automation-suite/2.2510/installation-guide/configuring-ms-sql-server#sql-requirements-for-process-mining).`)
      }
    }
    if (evalDbTypes.has('mssql')) {
      const mssqlProducts = getProductNamesForDbType(productServices, formData.products, 'mssql');
      const mssqlProductLabel = mssqlProducts.length > 0 ? mssqlProducts.join(', ') : 'selected products';
      sqlNodes.push(sqlNode);
      details.push(`* **${mssqlProductLabel}** requires a Shared SQL Server node with **${sqlNode.cpu}** / **${sqlNode.ram}**.`)
    }

    details.push("### Each Server Node Configuration")
    details.push(`* Requires **${formatCpu(nodeCpu)}** processors.`)
    details.push(`* Requires **${formatBytes(nodeRam)}** RAM.`)
    details.push(`* Requires **16 GiB SSD** Disk dedicated for storing **etcd** data.`)
    details.push(`* Requires **${osDisk}** Disk dedicated for storing **cluster state** data.`)
    details.push(`* Requires **${dataDisk}** Disk dedicated for storing **block** data.`)
    if (formData.highLevelParameters.host === "On-Prem" && !formData.highLevelParameters.externalS3) {
      if (profile === Profile.EvaluationOnly) {
        nodes[0].drives.push("512 GiB SSD");
        details.push(`* Requires **512 GiB SSD** Disk dedicated for storing backup of **objectstore(in-cluster ceph)**. The size of local disk should be based on objectstore usage and backup schedules.`)
      }
      nodes[0].drives.push("512 GiB SSD");
      details.push(`* Requires **512 GiB SSD** Disk dedicated for storing **objectstore** data.`)
    }
    if (formData.highLevelParameters.airGapped) {
      nodes[0].drives.push("512 GiB SSD");
      details.push(`* Requires **512 GiB SSD** Disk for temporarily unbundling the **offline** packages. This disk is only on the first server machine.`)
    }


    let recommendation : SiteResult = {
      nodes: nodes,
      debug: details,
      sqlNodes: sqlNodes,
      totalCpu: evalTotalCpuBeforeBuffer,
      totalRam: evalTotalRamBeforeBuffer,
      bufferedCpu: evalTotalCpu,
      bufferedRam: evalTotalRam
    }

    if (formData.highLevelParameters.backupEnabled && formData.highLevelParameters.host === "On-Prem") {
      recommendation["nfsNode"] = nfsNode;
    }

    return recommendation;
  }

  // Multi-nodes Production, HA ready
  let totalCpu = 0;
  let totalRam = 0;
  let totalDataDisk = 0;
  let totalPvc = 0;

  let faultCount = +formData.highLevelParameters.faultCount;
  let serverCount = 3;
  if (isASAirProfile(profile)) {
    serverCount = 4;
  }

  if (profile === Profile.Lite) {
    details.push("## Lite Mode")
  } else {
    details.push("## High Availability")
  }

  if (faultCount == 0) {
    details.push("A node fault tolerance of **0** doesn't mean the cluster won't survive one node going down. The cluster will still be functional but it will be in a degraded state (some services might be unavailable or with reduced capacity) because the total capacity of the cluster without that missing node is less than the sum of the capacity needed by each component (see below).")
  } else {
    // Let X be the number of server nodes and F be the number of faults.
    // To survive F faults, the quorum (X/2+1) needs to be guaranteed, so X should be greater or equal to 2*F+1
    serverCount = Math.max(serverCount, 2 * faultCount + 1);
    details.push(`To survive **${faultCount}** nodes going down:`);
    if(!isASAirProfile(profile)) {
      details.push(`* The cluster will need **${serverCount}** server nodes because in the worst case, these **${faultCount}** nodes will be server nodes which need to maintain a quorum of **${Math.floor(serverCount / 2) + 1}**.`);
      details.push(`* With **S** servers and **A** agent nodes, **S+A-${faultCount}** nodes need to have enough capacity to accomodate the resource requirements (see below).`)
      details.push(`* If dedicated nodes need to also be highly available, then they should also increase their count by **${faultCount}**. That being said, typically these node run background jobs, so real HA is not required.`)
    }else{
      details.push(`* The cluster will need **${serverCount}** worker nodes to maintain a quorum of **${Math.floor(serverCount / 2) + 1}**.`);
      details.push(`* With **W** worker nodes, **W-${faultCount}** nodes need to have enough capacity to accomodate the resource requirements (see below).`)
      details.push(`* If dedicated nodes need to also be highly available, then they should also increase their count by **${faultCount}**. That being said, typically these node run background jobs, so real HA is not required.`)

    }
  }

  details.push("## Resource Requirements")
  details.push("### Per Service")

  for (let product of effectiveProducts) {
    // Platform is handled in the Shared Suite Capabilities section below
    if (product.key === constants.PLATFORM_PRODUCT_OPTION.key) continue;

    const productBL = getProductBaselineProfile(product);

    const services = productServices.get(product.key) || [];
    const productProfile = profile === Profile.Lite ? convertProfile(product.profile) : profile;
    const { cpu, ram } = computeProductResources(
      services, product.key, distribution, productBL,
    );
    const haOverride = getProductOverride(product.key, distribution, productBL);

    totalCpu += cpu
    totalRam += ram

    const haHeadingIndex = details.length;
    details.push(`#### ${product.name}`);
    // Track this product's additional (on-demand + handler) contribution, for the Total line.
    let haAddCpu = 0;
    let haAddRam = 0;

    // Add AI Center note as the first point in the section
    if (product.key === constants.AI_CENTER_PRODUCT_OPTION.key) {
              details.push("* **Note:** Only incase you have overridden CPU and Memory limits for your AI Center Skill. Use VM with CPU = Skill CPU size + 2 vCPU and RAM = Skill RAM size + 2GB based on your requirement.");
    }

    // Base per-service line(s). A lone one is dropped later unless scaling keeps it useful.
    const haSvcStart = details.length;
    if (!haOverride) {
      renderServiceLineItems(services, distribution, productBL, details, serviceDisplayNames);
    }
    const haServiceLineCount = details.length - haSvcStart;

    const haPgLine = getProductPostgresLine(product.key);
    if (haPgLine) details.push(haPgLine);

    const haServerlessLine = getServerlessContributionLine(product.key);
    if (haServerlessLine) details.push(haServerlessLine);

    const haPreHandlerCpu = totalCpu;
    const haPreHandlerRam = totalRam;
    const haResult = dispatchProductHandler(product, details, totalCpu, totalRam, totalPvc, productHandlers, { profile: productProfile, services, baselineProfile: productBL });
    if (haResult) {
      totalCpu = round2Cpu(haResult.totalCpu);
      totalRam = round2Ram(haResult.totalRam);
      totalPvc = haResult.totalPvc || totalPvc;
    }
    haAddCpu += totalCpu - haPreHandlerCpu;
    haAddRam += totalRam - haPreHandlerRam;

    dropRedundantSingleServiceLine(details, haSvcStart, haServiceLineCount, !!haOverride, haAddCpu > 0 || haAddRam > 0);
    insertProductTotalLine(details, haHeadingIndex, cpu + haAddCpu, ram + haAddRam);

    pushProductDocLink(details, product.key);
  }

  // Shared Suite Capabilities (platform services resolved via graph)
  {
    const platformProduct = effectiveProducts.find((p: any) => p.key === constants.PLATFORM_PRODUCT_OPTION.key);
    const platformBL = getProductBaselineProfile(platformProduct);
    const platformServices = productServices.get(constants.PLATFORM_PRODUCT_OPTION.key) || [];
    let platformCpu = 0;
    let platformRam = 0;
    for (const svc of platformServices) {
      const res = getServiceResources(svc, distribution, platformBL);
      platformCpu += res.cpu;
      platformRam += res.ram;
    }
    totalCpu += platformCpu;
    totalRam += platformRam;

    const haPlatHeadingIndex = details.length;
    details.push(`#### Shared Suite Capabilities`);
    const haPlatSvcStart = details.length;
    renderServiceLineItems(platformServices, distribution, platformBL, details, serviceDisplayNames);
    const haPlatServiceLineCount = details.length - haPlatSvcStart;

    const haPlatPreHandlerCpu = totalCpu;
    const haPlatPreHandlerRam = totalRam;
    if (platformProduct) {
      const result = dispatchProductHandler(platformProduct, details, totalCpu, totalRam, totalPvc, productHandlers, { profile, services: platformServices, baselineProfile: platformBL });
      if (result) {
        totalCpu = round2Cpu(result.totalCpu);
        totalRam = round2Ram(result.totalRam);
        totalPvc = result.totalPvc || totalPvc;
        }
    }
    const haPlatAddCpu = totalCpu - haPlatPreHandlerCpu;
    const haPlatAddRam = totalRam - haPlatPreHandlerRam;

    dropRedundantSingleServiceLine(details, haPlatSvcStart, haPlatServiceLineCount, false, haPlatAddCpu > 0 || haPlatAddRam > 0);
    insertProductTotalLine(details, haPlatHeadingIndex, platformCpu + haPlatAddCpu, platformRam + haPlatAddRam);
  }

  // "All services" = user-selected products + Shared Suite Capabilities, captured before
  // fabric is added. The extra 10% headroom is applied to this sum.
  const allServicesCpu = totalCpu;
  const allServicesRam = totalRam;

  // Fabric is a separate product from Shared Suite Capabilities.
  // OpenShift deployments do not use Fabric — skip it for air-openshift.
  // Shared Cluster mode (AKS/EKS) also skips fabric — the host cluster supplies it.
  // Fabric is still counted in the total (when applicable) but is no longer shown as its
  // own line; its footprint together with the buffer headroom is surfaced as the
  // "Automation Suite Platform requirement" section below.
  if (!isOpenShift && !isSharedCluster) {
    const haFabricOverride = getProductOverride('fabric', distribution, baselineProfile);
    const fabricReqs = haFabricOverride || getProductResources('fabric', distribution, baselineProfile);
    totalCpu += fabricReqs.cpu;
    totalRam += fabricReqs.ram;
  }

  // Automation Suite Platform requirement = everything on top of the selected services:
  // the Service Fabric layer (fabric) plus the buffer headroom (20% of the full total +
  // 10% of all services). Equivalently, Total Requirement − services, so "Actual" (services)
  // and this line add up to the Total. Applied for all profiles.
  // NOTE: for multizonal clusters the Total Requirement additionally scales by the zone
  // factor; this line shows the pre-scale fabric + buffer, matching that concept.
  const platformReqCpu = round2Cpu(totalCpu * (1 + BUFFER_RATE) + allServicesCpu * HEADROOM_RATE) - allServicesCpu;
  const platformReqRam = round2Ram(totalRam * (1 + BUFFER_RATE) + allServicesRam * HEADROOM_RATE) - allServicesRam;
  pushPlatformRequirementSection(details, platformReqCpu, platformReqRam);
  if (isSharedCluster) {
    details.push(`* Fabric is excluded (shared cluster mode) — the host managed cluster provides the Service Fabric layer.`);
  }

  if (!isASAirProfile(profile)) {
    details.push(`* **High Availability Add-on (HAA)** can be enabled in the same configuration without adding additional capacity. Learn more in [Automation Suite - High Availability Add-on configuration](https://docs.uipath.com/automation-suite/automation-suite/2.2510/installation-guide/high-availability-add-on).`);
  }

  // --- Node capacity + headroom, computed before rendering the Total summary so the grand
  //     Total Requirement can lead the section (not be buried under Per Node). ---
  let staticCpu = (profile === Profile.HAProduction || profile === Profile.Lite) ? 600 :
      isASAirProfile(profile) ? 100 : 0;

  let staticRam = (profile === Profile.HAProduction || profile === Profile.Lite) ? 270 :
      isASAirProfile(profile) ? 100 : 0;

  let nodePhysicalCpu = formData.highLevelParameters.coreCount * 1000;
  let nodePhysicalRam = (formData.highLevelParameters.ramCount || formData.highLevelParameters.coreCount * 2) * 1024 * 1024 * 1024;

  let nodeAllocatableCpu = nodePhysicalCpu - 600;
  let nodeAllocatableRam = nodePhysicalRam - 2 * 1024 * 1024 * 1024;
  let nodeAvailableCpu = (profile === Profile.HAProduction || profile === Profile.Lite) ? nodeAllocatableCpu - staticCpu : isASAirProfile(profile) ? nodeAllocatableCpu - staticCpu: 0;
  let nodeAvailableRam = (profile === Profile.HAProduction || profile === Profile.Lite) ? nodeAllocatableRam - 270 * 1024 * 1024 : isASAirProfile(profile) ? nodeAllocatableRam - staticRam * 1024 * 1024: 0;

  let totalServerCPU = serverCount * nodeAvailableCpu;
  let totalAgentCPU = totalCpu - totalServerCPU;
  let minAgentNode = Math.ceil(totalAgentCPU / nodeAvailableCpu);
  let minCount = 0
  // Store totals before multizonal factor and buffer for summary display
  const totalCpuBeforeBuffer = totalCpu;
  const totalRamBeforeBuffer = totalRam;

  // Resource requirements in case of multizonal cluster
  if (formData.highLevelParameters.isMultizoanl) {
    let minCount = Math.ceil(Math.max(serverCount + minAgentNode, totalRam / nodeAvailableRam));
    let nodeCount = Math.max(serverCount, minCount + faultCount);
    let agentCount = Math.max(0, nodeCount - serverCount);
    const zoneFailureDelta = getMultizonalFactor(nodeCount);
    totalCpu = round2Cpu(totalCpu * zoneFailureDelta);
    totalRam = round2Ram(totalRam * zoneFailureDelta);
  }

  // Add breathing room: a 20% buffer on the full total, plus an extra 10% headroom on all
  // services (products + Shared Suite Capabilities). Applied for all profiles.
  const buffered = applyBufferAndHeadroom(totalCpu, totalRam, allServicesCpu, allServicesRam);
  totalCpu = buffered.cpu;
  totalRam = buffered.ram;

  // --- Total summary: services + Automation Suite Platform requirement = Total Requirement ---
  details.push("### Total");
  details.push(`* Actual (services): **${formatCpu(allServicesCpu)}** / **${formatBytes(allServicesRam * 1024 * 1024)}**`);
  details.push(`* Automation Suite Platform requirement: **${formatCpu(platformReqCpu)}** / **${formatBytes(platformReqRam * 1024 * 1024)}**`);
  details.push(`* Total Requirement: **${formatCpu(totalCpu)}** / **${formatBytes(totalRam * 1024 * 1024)}**`);
  if (totalPvc > 0) {
    details.push(`* Additional dedicated PVC storage: **${totalPvc}** GiB total.`);
  }

  // --- Per Node ---
  details.push("### Per Node");
  details.push("Not all the CPU / RAM of the nodes is available to run the services:")
  details.push("* Each node reserves **0.6 vCPU** / **2 GiB** for the OS.");

  if(profile === Profile.HAProduction || profile === Profile.Lite) {
    details.push(`* Each node has static components (logging, metrics, etc...) reserving **${formatCpu(staticCpu)}** / **270 MiB**.`)
  }

  if(isASAirProfile(profile)) {
    details.push(`* Each node has static components (logging, monitoring, metrics, etc...) reserving **${formatCpu(staticCpu)}** / **100 MiB**.`)
  }

  totalAgentCPU = totalCpu - totalServerCPU;
  minAgentNode = Math.ceil(totalAgentCPU / nodeAvailableCpu);

  if (!isASAirProfile(profile)) {
   minCount = Math.ceil(Math.max(serverCount + minAgentNode, totalRam * 1024 * 1024 / nodeAvailableRam));
  }else{
    minCount = Math.ceil(Math.max(serverCount , totalRam * 1024 * 1024 / nodeAvailableRam));
  }

  let nodeCount = Math.max(serverCount, minCount + faultCount);
  let agentCount = Math.max(0, nodeCount - serverCount);
  let finalNodeCount = Math.ceil(Math.max(totalCpu/nodePhysicalCpu, totalRam * 1024 * 1024 / nodePhysicalRam))

  details.push("## Recommendations");
  if (!isASAirProfile(profile)) {
    details.push(`* A **${formatCpu(nodePhysicalCpu)}** / **${formatBytes(nodePhysicalRam)}** has an *allocatable* capacity of **${formatCpu(nodeAllocatableCpu)}** / **${formatBytes(nodeAllocatableRam)}**, of which only **${formatCpu(nodeAvailableCpu)}** / **${formatBytes(nodeAvailableRam)}** is *available* for node.`);
    details.push(`* Therefore, to schedule **${formatCpu(totalCpu)}** / **${formatBytes(totalRam * 1024 * 1024)}** worth of services, at least **${minCount}** nodes are required.`);
    details.push(`* But to survive **${faultCount}** node failures, **${minCount} + ${faultCount} = ${minCount + faultCount}** nodes are required.`)
    details.push(`* Out of which, **${serverCount}** server nodes and, **${agentCount}** agent nodes are required.`)

    switch (formData.highLevelParameters.host) {
      case "AWS":
        details.push(`* You will need a shared **SQL server** and **AWS S3 Buckets** for the selected products.`);
        break;
      case "Azure":
        details.push(`* You will need a shared **SQL server** and **Azure Blob Storage** for the selected products.`);
        break;
      case "On-Prem":
        if (formData.highLevelParameters.externalS3) {
          details.push(`* You will need a shared **SQL server** and **S3 Compatible Object Store** for the selected products.`);
        } else {
          details.push(`* You will need a shared **SQL server** for the selected products.`);
        }
        break;
    }
  }else{

    details.push(`* A **${formatCpu(nodePhysicalCpu)}** / **${formatBytes(nodePhysicalRam)}** has an *allocatable* capacity of **${formatCpu(nodeAllocatableCpu)}** / **${formatBytes(nodeAllocatableRam)}**, of which only  **${formatCpu(nodeAvailableCpu)}** / **${formatBytes(nodeAvailableRam)}** is *available* for a worker node.`);
    details.push(`* Therefore, to schedule **${formatCpu(totalCpu)}** / **${formatBytes(totalRam * 1024 * 1024)}** worth of services, at least **${finalNodeCount}** nodes are required.`);
    details.push(`* But to survive **${faultCount}** node failures, **${finalNodeCount + faultCount}** nodes are required.`)

    switch (formData.highLevelParameters.deploymentType) {
      case "air-eks":
        details.push(`* You will need a shared **SQL server** and **AWS S3 Buckets** for the selected products.`);
        details.push(`* Start with **500 GiB** for the Object Store. Final size depends on the deployed automation; see [Storage sizing for Automation Suite on EKS/AKS](https://docs.uipath.com/automation-suite/automation-suite/2.2510/installation-guide-eks-aks/storage).`);
        details.push(`* You will need a **100 GiB** of **EBS** for monitoring and **20 GiB** of **EBS** for logging .`);
        if (isStudioWebEnabled) {
          details.push(`* **Studio Web** needs **AWS ElasticFileStore** for storing NuGet packages (220 GiB) and user projects (50 GiB).`)
        }
        if (isISEnabled) {
          details.push(`* **Integration Services** needs **AWS Simple Queue Service**.`)
        }
        break;
      case "air-aks":
        details.push(`* You will need a shared **SQL server** and **Azure Blob Storage** for the selected products.`);
        details.push(`* Start with **500 GiB** for the Object Store. Final size depends on the deployed automation; see [Storage sizing for Automation Suite on EKS/AKS](https://docs.uipath.com/automation-suite/automation-suite/2.2510/installation-guide-eks-aks/storage).`);
        details.push(`* You will need a **100 GiB** of **Azure Manage Disk** for monitoring and **20 GiB** of **Azure Manage Disk** for logging .`);
        if (isStudioWebEnabled) {
          details.push(`* **Studio Web** needs **Azure FileStore** for storing NuGet packages (220 GiB) and user projects (50 GiB).`)
        }
        if (isISEnabled) {
          details.push(`* **Integration Services** needs **Azure Queue Storage**.`)
        }
        break;
      case "air-openshift":
        details.push(`* You will need a shared **SQL server** and **S3 or OpenShift Data Foundation** for the selected products.`);
        details.push(`* Start with **500 GiB** for the Object Store. Final size depends on the deployed automation; see [Storage sizing for Automation Suite on OpenShift](https://docs.uipath.com/automation-suite/automation-suite/2.2510/installation-guide-openshift/storage).`);
        if (isStudioWebEnabled) {
          details.push(`* **Studio Web** needs a **Persistent Volume** for storing NuGet packages (220 GiB) and user projects (50 GiB).`)
        }
        break;
    }
  }
  // Rounding up
  totalDataDisk = Math.ceil(totalDataDisk);

  let nodes = new Array<NodeDataType>();
  let sqlNodes = new Array<NodeDataType>();
  let drivesArray = [];
  let type = ""
  let toolTip = ""
  let count = ""
  if (!isASAirProfile(profile)) {
    drivesArray.push("16 GiB SSD", osDisk, dataDisk);
    type = serverType
    toolTip = serverTypeTooltip
    count = serverCount.toString()
  }else{
    drivesArray.push(airOsDisk);
    type = workerType
    toolTip = workerTypeTooltip
    count = faultCount != 0 ? (finalNodeCount + faultCount).toString() :  finalNodeCount.toString()
  }

  // For Air-Gapped Multi-Node deployments, split server nodes into first and remaining
  if (!isASAirProfile(profile) && formData.highLevelParameters.airGapped && serverCount > 1) {
    // First server node with 512 GB disk for registry seeding
    let firstServerDrives = [...drivesArray];
    if (formData.highLevelParameters.host === "On-Prem" && !formData.highLevelParameters.externalS3) {
      firstServerDrives.push("512 GiB SSD"); // objectstore disk
    }
    firstServerDrives.push("512 GiB SSD"); // offline packages disk

    nodes.push({
      nodeType: type,
      nodeTypeTooltip: toolTip,
      nodeCount: "1",
      nodeCountTooltip: `First server node with additional disk for offline packages.`,
      cpu: formatCpu(nodePhysicalCpu),
      ram: formatBytes(nodePhysicalRam),
      drives: firstServerDrives,
    });

    // Remaining server nodes without 512 GB offline packages disk
    let remainingServerDrives = [...drivesArray];
    if (formData.highLevelParameters.host === "On-Prem" && !formData.highLevelParameters.externalS3) {
      remainingServerDrives.push("512 GiB SSD"); // objectstore disk on all servers
    }

    nodes.push({
      nodeType: type,
      nodeTypeTooltip: toolTip,
      nodeCount: (serverCount - 1).toString(),
      nodeCountTooltip: `Remaining server nodes.`,
      cpu: formatCpu(nodePhysicalCpu),
      ram: formatBytes(nodePhysicalRam),
      drives: remainingServerDrives,
    });
  } else {
    // Standard node creation for non-Air-Gapped or single-node deployments
    nodes.push({
      nodeType: type,
      nodeTypeTooltip: toolTip,
      nodeCount: count,
      nodeCountTooltip: `An odd number of Server nodes. At least ${serverCount}.`,
      cpu: formatCpu(nodePhysicalCpu),
      ram: formatBytes(nodePhysicalRam),
      drives: drivesArray,
    });
  }

  if (!isASAirProfile(profile)) {
    if (agentCount > 0) {
      nodes.push({
        nodeType: agentType,
        nodeTypeTooltip: agentTypeTooltip,
        nodeCount: agentCount.toString(),
        nodeCountTooltip: `See details below.`,
        cpu: formatCpu(nodePhysicalCpu),
        ram: formatBytes(nodePhysicalRam),
        drives: [
          "256 GiB SSD",
        ],
      });
    }
  }

  if (duGpuCount > 0) {
    nodes.push(duNode(duGpuCount))
    details.push(`* **Document Understanding** needs a dedicated node.`)
  }

  populateDUModernExperienceGPUNodeInfo(isModernExperienceEnabled, modernExperienceGpuInfo, nodes);

  const serverlessNodeCpuHA = asRobotNodeCpu + studioWebNodeCpu;
  const serverlessNodeRamHA = asRobotNodeRam + studioWebNodeRam;
  const serverlessNodeDiskHA = asRobotNodeDisk + studioWebNodeDisk;

  if (!isASAirProfile(profile)) {
    asRobotdisk.push("256 GiB SSD", serverlessNodeDiskHA + " GiB SSD");
  }

  if (serverlessNodeCpuHA > 0 || serverlessNodeRamHA > 0) {
    const serverlessNodeCount = Math.ceil(Math.max(serverlessNodeCpuHA / AS_ROBOT_NODE_CPU, serverlessNodeRamHA / AS_ROBOT_NODE_RAM));
    const serverlessProducts: string[] = [];
    if (isAutomationSuiteRobotsEnabled) serverlessProducts.push("Automation Suite Robots");
    if (isStudioWebEnabled && studioWebNodeCpu > 0) serverlessProducts.push("Studio Web");
    const serverlessTooltip = `Node for ${serverlessProducts.join(' and ')}`;
    nodes.push({
      nodeType: asRobotsType,
      nodeTypeTooltip: serverlessTooltip,
      nodeCount: serverlessNodeCount.toString(),
      cpu: "8 vCPU",
      ram: "32 GiB",
      drives: asRobotdisk,
    });
    details.push(`* **${serverlessProducts.join(' and ')}** needs ${serverlessNodeCount} dedicated node${serverlessNodeCount > 1 ? 's' : ''} and **10 GiB** of file storage`)
  }

  const haDbTypes = getRequiredDatabaseTypes(allResolvedServices);
  if (isProcessMiningEnabled) {
    sqlNodes.push(pmNode);
    details.push(`* **Process Mining** requires a dedicated SQL Server node with **${pmNode.cpu}** / **${pmNode.ram}**.`)
  }
  if (haDbTypes.has('postgresql') || pmProductSel || maestroProduct || a4dProduct) {
    const shared = buildSharedPostgresNode();
    if (shared) {
      sqlNodes.push(shared.node);
      details.push(...shared.detailLines);
    } else if (haDbTypes.has('postgresql')) {
      sqlNodes.push(postgresNode);
      const pgProducts = getProductNamesForDbType(productServices, formData.products, 'postgresql');
      const pgProductLabel = pgProducts.length > 0 ? pgProducts.join(', ') : 'Process Mining';
      details.push(`* **${pgProductLabel}** requires a PostgreSQL Server node with **${postgresNode.cpu}** / **${postgresNode.ram}**, see [database requirements](https://docs.uipath.com/automation-suite/automation-suite/2.2510/installation-guide/configuring-ms-sql-server#sql-requirements-for-process-mining).`)
    }
  }
  if (haDbTypes.has('mssql')) {
    const mssqlProducts = getProductNamesForDbType(productServices, formData.products, 'mssql');
    const mssqlProductLabel = mssqlProducts.length > 0 ? mssqlProducts.join(', ') : 'selected products';
    sqlNodes.push(sqlNode);
    details.push(`* **${mssqlProductLabel}** requires a Shared SQL Server node with **${sqlNode.cpu}** / **${sqlNode.ram}**.`)
  }

  let recommendation : SiteResult = {
    nodes: nodes,
    debug: details,
    sqlNodes: sqlNodes,
    totalCpu: totalCpuBeforeBuffer,
    totalRam: totalRamBeforeBuffer,
    bufferedCpu: totalCpu,
    bufferedRam: totalRam
  }

  if (formData.highLevelParameters.backupEnabled && !isASAirProfile(profile) && formData.highLevelParameters.host === "On-Prem") {
    recommendation["nfsNode"] = nfsNode;
    details.push(`* **Enabling Backup** will need an NFS Server or equivalent PaaS file share.`)
  }
  if (!isASAirProfile(profile)) {
    details.push("### Each Server Node Configuration")
    details.push(`* Requires **${formatCpu(nodePhysicalCpu)}** processors.`);
    details.push(`* Requires **${formatBytes(nodePhysicalRam)}** RAM.`);
    details.push(`* Requires **16 GiB SSD** Disk dedicated for storing **etcd** data.`);
    details.push(`* Requires **${osDisk}** Disk dedicated for storing **cluster state** data.`);
    details.push(`* Requires **${dataDisk}** Disk dedicated for storing **block** data.`);
  }else {
    details.push("### Each Worker Node Configuration")
    details.push(`* Requires **${formatCpu(nodePhysicalCpu)}** processors.`);
    details.push(`* Requires **${formatBytes(nodePhysicalRam)}** RAM.`);
    details.push(`* Requires **${airOsDisk}** OS Disk.`);
  }
  if (!isASAirProfile(profile)) {
    // For non-Air-Gapped deployments, add objectstore disk to all server nodes
    if (formData.highLevelParameters.host === "On-Prem" && !formData.highLevelParameters.externalS3 && !formData.highLevelParameters.airGapped) {
      nodes[0].drives.push("512 GiB SSD");
      details.push(`* Requires **512 GiB SSD** Disk dedicated for storing **objectstore** data.`);
    }
    // Add details for Air-Gapped deployments (disks already added in node creation above)
    if (formData.highLevelParameters.airGapped) {
      if (formData.highLevelParameters.host === "On-Prem" && !formData.highLevelParameters.externalS3) {
        details.push(`* Requires **512 GiB SSD** Disk dedicated for storing **objectstore** data on all server nodes.`);
      }
      details.push(`* Requires **512 GiB SSD** Disk for temporarily unbundling the **offline** packages. This disk is only on the first server machine.`);
    }
  }
  if (!isASAirProfile(profile)) {
    if (agentCount > 0) {
      details.push("### Each Agent Node Configuration")
      details.push(`* Requires **${formatCpu(nodePhysicalCpu)}** processors.`)
      details.push(`* Requires **${formatBytes(nodePhysicalRam)}** RAM.`)
      details.push(`* Requires **256 GiB SSD** Disk dedicated for storing **cluster state** data.`)
    }

    details.push(`> **etcd**, **block**, and **objectstore** disk must be a dedicated disk on each server node, the rest can be merged together or even with the operating system disk.`)
  }
  return recommendation;
};

export const calculateRequirements = (
  formData: CalculatorFormDataType
): RecommendationDataType => {
  const hlp = formData.highLevelParameters;
  const apOn = !!hlp.apMode && isApAvailable(hlp.deploymentType, hlp.version);

  const primary = computeSiteRequirements(formData, apOn ? 'primary' : undefined);
  const result: RecommendationDataType = { primary };
  if (primary.error) result.error = primary.error;

  if (apOn) {
    // Derive the secondary-site plan from the service-rooted A/P ban list:
    // `excludedProductKeys` filters the product set (consumer cascade +
    // all-or-nothing owner rule); `bannedServices` (the closed ban set) is
    // stripped from the secondary resolution below so a banned service never
    // contributes to passive sizing even when a partialSelection owner (e.g.
    // platform's taas) force-includes it via an explicit key.
    const distribution = getDistribution(hlp.deploymentType);
    const plan = deriveSecondaryPlan(hlp.deploymentType, hlp.version, distribution);
    const filteredExplicit = formData.explicitKeys.filter(
      (k) => !plan.excludedProductKeys.has(k)
    );
    const filteredProducts = formData.products.filter(
      (p) => !plan.excludedProductKeys.has(p.key)
    );
    const secondaryFormData: CalculatorFormDataType = {
      ...formData,
      explicitKeys: filteredExplicit,
      products: filteredProducts,
    };
    const secondary = computeSiteRequirements(secondaryFormData, 'secondary', plan.bannedServices);
    result.secondary = secondary;
    if (secondary.error && !result.error) result.error = secondary.error;
  }

  return result;
};
